import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/ToastContainer";
import { blogService } from "../services/blogService";
import { userService } from "../services/userService";
import { courseService } from "../services/courseService";
import { uploadImage, uploadAttachment } from "../services/uploadService";
import { parseApiError } from "../utils/errorHandler";
import {
  ArrowLeft,
  X,
  Plus,
  Loader2,
  Save,
  FileText,
  Image as ImageIcon,
  Paperclip,
} from "lucide-react";

interface Course {
  _id: string;
  title: string;
}

interface Author {
  _id: string;
  firstName: string;
  lastName: string;
  role: string;
}

export default function AddEditBlogPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const { showSuccess, showError } = useToast();

  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingThumb, setIsUploadingThumb] = useState(false);
  const [isUploadingAttach, setIsUploadingAttach] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);

  const [formData, setFormData] = useState<any>({
    title: "",
    slug: "",
    description: "",
    content: "",
    type: "article",
    thumbnail: "",
    attachments: [],
    tags: "",
    author: "",
    relatedCourse: "",
    isPublished: false,
  });

  // Fetch dropdown data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [coursesRes, adminsRes, instructorsRes] = await Promise.all([
          courseService.getAllCourses({ limit: 100 }),
          userService.getAllUsers({ role: "admin" }),
          userService.getAllUsers({ role: "instructor" }),
        ]);
        setCourses((coursesRes as any).data?.courses || []);

        const allAuthors = [
          ...((adminsRes as any).users || []),
          ...((instructorsRes as any).users || []),
        ];
        setAuthors(allAuthors);
      } catch (err) {
        console.error("Failed to fetch dropdown data:", err);
      }
    };
    fetchData();
  }, []);

  // Fetch blog data if editing
  const fetchBlog = useCallback(async () => {
    if (!id) return;
    setIsFetching(true);
    try {
      const response = await blogService.getBlogById(id);
      const blog = (response as any).blog;
      setFormData({
        title: blog.title || "",
        slug: blog.slug || "",
        description: blog.description || "",
        content: blog.content || "",
        type: (blog.type as any) || "article",
        thumbnail: blog.thumbnail || "",
        attachments: blog.attachments || [],
        tags: blog.tags?.join(", ") || "",
        author: blog.author?._id || blog.author || "",
        relatedCourse:
          (blog.relatedCourse as any)?._id ||
          (blog.relatedCourse as string) ||
          "",
        isPublished: blog.isPublished || false,
      });
    } catch (err) {
      const apiError = parseApiError(err);
      showError(apiError.message);
      navigate("/blogs");
    } finally {
      setIsFetching(false);
    }
  }, [id, navigate, showError]);

  useEffect(() => {
    if (isEditMode) fetchBlog();
  }, [isEditMode, fetchBlog]);

  // Auto-generate slug
  useEffect(() => {
    if (!isEditMode && formData.title) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
      setFormData((prev: any) => ({ ...prev, slug }));
    }
  }, [formData.title, isEditMode]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    isThumbnail: boolean
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (isThumbnail) setIsUploadingThumb(true);
    else setIsUploadingAttach(true);

    try {
      if (isThumbnail) {
        const url = await uploadImage(file, "blogs");
        setFormData((prev: any) => ({ ...prev, thumbnail: url }));
        showSuccess("Thumbnail uploaded");
      } else {
        const url = await uploadAttachment(file);

        // Map MIME type to backend fileType enum
        let fileType = "other";
        const mime = file.type.toLowerCase();
        const extension = file.name.split(".").pop()?.toLowerCase();

        if (mime === "application/pdf" || extension === "pdf") fileType = "pdf";
        else if (
          mime === "application/msword" ||
          extension === "doc" ||
          extension === "docx"
        )
          fileType = "docx";
        else if (
          mime === "application/vnd.ms-powerpoint" ||
          extension === "ppt" ||
          extension === "pptx"
        )
          fileType = "pptx";
        else if (
          mime === "application/vnd.ms-excel" ||
          extension === "xls" ||
          extension === "xlsx"
        )
          fileType = "xlsx";
        else if (
          mime.includes("zip") ||
          extension === "zip" ||
          extension === "rar"
        )
          fileType = "zip";

        const attachment = {
          name: file.name,
          url,
          fileType,
          size: file.size,
        };

        setFormData((prev: any) => ({
          ...prev,
          attachments: [...prev.attachments, attachment],
        }));
        showSuccess("Attachment uploaded");
      }
    } catch (err: any) {
      showError(err.message || "Upload failed");
    } finally {
      setIsUploadingThumb(false);
      setIsUploadingAttach(false);
    }
  };

  const removeAttachment = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      attachments: prev.attachments.filter((_: any, i: number) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const tags = formData.tags
        .split(",")
        .map((t: string) => t.trim())
        .filter((t: string) => t);
      const payload = {
        ...formData,
        tags,
        relatedCourse: formData.relatedCourse || undefined,
      };
      if (isEditMode && id) {
        await blogService.updateBlog(id, payload);
        showSuccess("Blog updated successfully");
      } else {
        await blogService.createBlog(payload);
        showSuccess("Blog created successfully");
      }
      navigate("/blogs");
    } catch (err: any) {
      const apiError = parseApiError(err);
      showError(apiError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isFetching) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#e07828]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <form onSubmit={handleSubmit} className="max-w-5xl mx-auto pb-20">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/blogs"
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[#102040]">
                {isEditMode ? "Edit Blog Post" : "Create New Blog"}
              </h1>
              <p className="text-gray-500 text-sm">
                {isEditMode
                  ? "Update your blog content and settings"
                  : "Draft a new story for your audience"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/blogs")}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className="min-w-[140px]"
            >
              {isSubmitting ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  <Save size={18} />{" "}
                  {isEditMode ? "Save Changes" : "Create Blog"}
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Title *
                </label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g. 10 Tips for Professional Traders"
                  required
                  className="h-12 text-lg font-medium"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Slug *
                </label>
                <Input
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  placeholder="slug-path"
                  required
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Unique URL identifier
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Short Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-white border border-[#DCE0E5] rounded-[8px] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#e07828]/20 focus:border-[#e07828]"
                  placeholder="Brief summary shown in lists..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Content *
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  rows={15}
                  className="w-full px-4 py-3 bg-white border border-[#DCE0E5] rounded-[8px] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#e07828]/20 focus:border-[#e07828] font-serif leading-relaxed"
                  placeholder="Write your blog post content here..."
                  required
                />
              </div>
            </Card>

            <Card className="space-y-4">
              <h3 className="font-bold text-[#102040] flex items-center gap-2">
                <Paperclip size={18} /> Attachments
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.attachments.map((att: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 group"
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <FileText size={20} className="text-gray-400 shrink-0" />
                      <span className="text-xs text-gray-600 truncate">
                        {att.name ||
                          (typeof att === "string"
                            ? att.split("/").pop()
                            : "File")}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(idx)}
                      className="p-1 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#e07828] hover:bg-amber-50/30 transition-all">
                  <input
                    type="file"
                    onChange={(e) => handleFileUpload(e, false)}
                    className="hidden"
                  />
                  {isUploadingAttach ? (
                    <Loader2
                      size={24}
                      className="animate-spin text-[#e07828]"
                    />
                  ) : (
                    <>
                      <Plus size={24} className="text-gray-300 mb-1" />{" "}
                      <span className="text-xs text-gray-500">Add File</span>
                    </>
                  )}
                </label>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Publish Status
                </label>
                <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleChange}
                    className="w-5 h-5 rounded border-gray-300 text-[#e07828] focus:ring-[#e07828]"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Published
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Blog Thumbnail *
                </label>
                <div className="relative group">
                  {formData.thumbnail ? (
                    <div className="aspect-video rounded-xl overflow-hidden border border-gray-200 shadow-sm relative">
                      <img
                        src={formData.thumbnail}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev: any) => ({
                            ...prev,
                            thumbnail: "",
                          }))
                        }
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center aspect-video border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#e07828] hover:bg-amber-50/30 transition-all">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, true)}
                        className="hidden"
                      />
                      {isUploadingThumb ? (
                        <Loader2
                          size={32}
                          className="animate-spin text-[#e07828]"
                        />
                      ) : (
                        <>
                          <ImageIcon size={32} className="text-gray-300 mb-2" />
                          <span className="text-sm text-gray-500">
                            Upload Thumbnail
                          </span>
                          <span className="text-[10px] text-gray-400 mt-1">
                            Recommended: 1200x630px
                          </span>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Type *
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="w-full h-11 px-4 py-2 bg-white border border-[#DCE0E5] rounded-[8px] text-sm focus:outline-none focus:ring-2 focus:ring-[#e07828]/20"
                  required
                >
                  <option value="article">Article</option>
                  <option value="tutorial">Tutorial</option>
                  <option value="file">Resource/File</option>
                  <option value="slide">Slideshow</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Author *
                </label>
                <select
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  className="w-full h-11 px-4 py-2 bg-white border border-[#DCE0E5] rounded-[8px] text-sm focus:outline-none focus:ring-2 focus:ring-[#e07828]/20"
                  required
                >
                  <option value="">Select Author</option>
                  {authors.map((author) => (
                    <option key={author._id} value={author._id}>
                      {author.firstName} {author.lastName} ({author.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Related Course
                </label>
                <select
                  name="relatedCourse"
                  value={formData.relatedCourse}
                  onChange={handleChange}
                  className="w-full h-11 px-4 py-2 bg-white border border-[#DCE0E5] rounded-[8px] text-sm focus:outline-none focus:ring-2 focus:ring-[#e07828]/20"
                >
                  <option value="">None</option>
                  {courses.map((course) => (
                    <option key={course._id} value={course._id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Tags
                </label>
                <Input
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  placeholder="trading, crypto, basic"
                />
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Comma separated
                </p>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </DashboardLayout>
  );
}
